import { getPhoneCallOutcomeLabel } from "../../models/phoneCallOutcomes";
import { getMeetingOutcomeLabel, getMeetingStatusLabel } from "../../models/meetingOutcomes";
import { useEffect, useMemo, useRef, useState } from "react";
import { createContactNote, getContactByID, getContactNotes, getContactStageHistory, updateContact } from "../../services/contact_service";
import { getPhoneCalls } from "../../services/conversation_service";
import { getMeetings } from "../../services/meeting_service";
import useContactEmailLog from "../../components/EmailLog/useContactEmailLog";
import ActivityTab from "./tabs/ActivityTab";
import EmailsTab from "./tabs/EmailsTab";
import CallsTab from "./tabs/CallsTab";
import FollowUpTab from "./tabs/FollowUpTab";
import DealsTab from "./tabs/DealsTab";
import MeetingsTab from "./tabs/MeetingsTab";
import NotesTab from "./tabs/NotesTab";
import TabView from "../../components/TabView/TabView";
import { deriveContactStageModel } from "../../components/ContactStage/contactStageModel";
import ContactStage from "../../components/ContactStage/ContactStage";
import NewContact from "../../components/modals/NewContact";
import { AccountDetailsModal } from "../Accounts";
import { getContactDetailDisplayData } from "./utils/contactDetailUtils";
import { getLeadForgeAssessment } from "../../services/account_service";
import "./ContactDetail.css";
import { EntityDetailPanel, Page, QuickStatsWidget } from "@iq/models";
import { FaRegBuilding } from "react-icons/fa";
import { FaRegNoteSticky } from "react-icons/fa6";
import { FiBriefcase, FiCalendar, FiDollarSign, FiMail, FiPhone, FiZap } from "react-icons/fi";
import { LuHandshake } from "react-icons/lu";
import { MdOutlineModeEdit, MdOutlineTouchApp } from "react-icons/md";
import useAppDataStore from "../../../states/useAppDataStore";
import useDealStore from "../../states/useDealStore";
import useAuthStore from "../../../states/authStore";

const TABS = ["Activity", "Emails", "Calls", "Follow-Up", "Deals", "Meetings", "Notes"];
const EMPTY_ARRAY = [];

const TAB_COMPONENTS = {
    Activity: ActivityTab,
    Emails: EmailsTab,
    Calls: CallsTab,
    "Follow-Up": FollowUpTab,
    Deals: DealsTab,
    Meetings: MeetingsTab,
    Notes: NotesTab,
};

function ContactDetail({
    contactId,
    initialContact = null,
    onBack,
    setPageHeader,
    followUps: sharedFollowUps = EMPTY_ARRAY,
    followUpsLoading: sharedFollowUpsLoading = false,
    followUpsError: sharedFollowUpsError = "",
    onFollowUpsRefresh,
    onFollowUpsForContactUpdate,
}) {
    const onBackRef = useRef(onBack);
    const headerInitializedRef = useRef(false);

    useEffect(() => {
        onBackRef.current = onBack;
    }, [onBack]);

    const profileName = useAuthStore((state) => state.profile?.name);

    const [contact, setContact] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [activeTab, setActiveTab] = useState("Activity");
    const [calls, setCalls] = useState([]);
    const [callsLoading, setCallsLoading] = useState(false);
    const [callsError, setCallsError] = useState("");
    const [meetings, setMeetings] = useState([]);
    const [meetingsLoading, setMeetingsLoading] = useState(false);
    const [meetingsError, setMeetingsError] = useState("");
    const [notes, setNotes] = useState([]);
    const [notesLoading, setNotesLoading] = useState(false);
    const [notesError, setNotesError] = useState("");
    const [notesSaving, setNotesSaving] = useState(false);
    const [stageHistory, setStageHistory] = useState([]);
    const [stageHistoryLoading, setStageHistoryLoading] = useState(false);
    const [stageHistoryError, setStageHistoryError] = useState("");
    const [noteDraft, setNoteDraft] = useState("");
    const [followUps, setFollowUps] = useState([]);
    const [followUpsLoading, setFollowUpsLoading] = useState(false);
    const [followUpsError, setFollowUpsError] = useState("");
    const followUpsByContactId = useAppDataStore((state) => state.followUpsByContactId);
    const deals = useDealStore((state) => state.deals);
    const dealsLoading = useDealStore((state) => state.loading);
    const dealsError = useDealStore((state) => state.error);
    const refreshDeals = useDealStore((state) => state.refreshDeals);
    const [tabDataVersion, setTabDataVersion] = useState(0);
    const [isEditing, setIsEditing] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [editError, setEditError] = useState("");
    const [isEditContactModalOpen, setIsEditContactModalOpen] = useState(false);
    const [accountAssessment, setAccountAssessment] = useState(null);
    const [accountAssessmentLoading, setAccountAssessmentLoading] = useState(false);
    const [accountAssessmentError, setAccountAssessmentError] = useState("");
    const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);

    const [panelActionRequest, setPanelActionRequest] = useState(null);

    const emailLog = useContactEmailLog({
        contactId,
        contact,
        initialContact,
        activeTab,
        refreshToken: tabDataVersion,
        onRefreshRequested: () => setTabDataVersion((value) => value + 1),
    });

    useEffect(() => {
        if (!contactId) return;
        let cancelled = false;
        setLoading(true);
        setError("");

        getContactByID(contactId)
            .then((data) => {
                if (cancelled) return;
                setContact(data);
                setEditForm({
                    firstName: data.first_name || "",
                    lastName: data.last_name || "",
                    email: data.email || "",
                    phone: data.phone || "",
                    mobile: data.mobile || data.phone || "",
                    jobTitle: data.title || "",
                    company: data.company || "",
                });
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err?.message || "Failed to load contact");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, [contactId]);

    useEffect(() => {
        const accountId = contact?.account_id;
        if (!accountId) {
            setAccountAssessment(null);
            setAccountAssessmentError("");
            return;
        }

        let cancelled = false;
        setAccountAssessmentLoading(true);
        setAccountAssessmentError("");
        getLeadForgeAssessment(accountId)
            .then((assessment) => {
                if (!cancelled) setAccountAssessment(assessment || null);
            })
            .catch((err) => {
                if (!cancelled) {
                    setAccountAssessment(null);
                    setAccountAssessmentError(err?.message || "Failed to load account sales context");
                }
            })
            .finally(() => {
                if (!cancelled) setAccountAssessmentLoading(false);
            });

        return () => { cancelled = true; };
    }, [contact?.account_id]);

    useEffect(() => {
        if (!contactId) {
            setCalls([]);
            setCallsLoading(false);
            setMeetings([]);
            setMeetingsError("");
            setMeetingsLoading(false);
            setNotes([]);
            setNotesError("");
            setNotesLoading(false);
            setNoteDraft("");
            setStageHistory([]);
            setStageHistoryError("");
            setStageHistoryLoading(false);
            return;
        }

        let cancelled = false;
        setCallsLoading(true);
        setMeetingsLoading(true);
        setNotesLoading(true);
		setStageHistoryLoading(true);
        setCallsError("");
        setMeetingsError("");
        setNotesError("");
		setStageHistoryError("");

        getPhoneCalls({ contactId })
            .then((payload) => {
                if (!cancelled) {
                    setCalls(Array.isArray(payload?.calls) ? payload.calls : []);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setCalls([]);
                    setCallsError(err?.message || "Failed to load call logs");
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setCallsLoading(false);
                }
            });
    
        getMeetings({ contactId })
            .then((rows) => {
                if (!cancelled) {
                    setMeetings(Array.isArray(rows) ? rows : []);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setMeetings([]);
                    setMeetingsError(err?.message || "Failed to load meetings");
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setMeetingsLoading(false);
                }
            });

        getContactNotes(contactId)
            .then((rows) => {
                if (!cancelled) {
                    setNotes(Array.isArray(rows) ? rows : []);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setNotes([]);
                    setNotesError(err?.message || "Failed to load notes");
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setNotesLoading(false);
                }
            });

        getContactStageHistory(contactId)
            .then((rows) => {
                if (!cancelled) setStageHistory(Array.isArray(rows) ? rows : []);
            })
            .catch((err) => {
                if (!cancelled) {
                    setStageHistory([]);
                    setStageHistoryError(err?.message || "Failed to load stage history");
                }
            })
            .finally(() => {
                if (!cancelled) setStageHistoryLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [contactId, tabDataVersion]);

    async function handleAddNote(content) {
        const trimmed = String(content || "").trim();
        if (!trimmed || !contactId) {
            return;
        }

        setNotesSaving(true);
        setNotesError("");

        try {
            const created = await createContactNote(contactId, trimmed);
            setNotes((prev) => [created, ...(Array.isArray(prev) ? prev : [])]);
            setNoteDraft("");
        } catch (err) {
            setNotesError(err?.message || "Failed to save note");
        } finally {
            setNotesSaving(false);
        }
    }

    useEffect(() => {
        if (!contactId) {
            setFollowUps([]);
            setFollowUpsError("");
            setFollowUpsLoading(false);
            return;
        }

        setFollowUpsLoading(sharedFollowUpsLoading);

        const lookupRows = followUpsByContactId[String(contactId)] || [];
        const fromShared = Array.isArray(sharedFollowUps)
            ? sharedFollowUps.filter((item) => Number(item?.contact_id ?? item?.contactId) === Number(contactId))
            : [];
        const rows = lookupRows.length > 0 ? lookupRows : fromShared;

        setFollowUps(rows);
        setFollowUpsError(sharedFollowUpsError || "");

        // Only seed shared lookup when this contact is missing in store.
        // Calling on every render causes a parent/store update loop.
        if (lookupRows.length === 0 && fromShared.length > 0) {
            onFollowUpsForContactUpdate?.(contactId, fromShared);
        }
    }, [
        contactId,
        sharedFollowUps,
        sharedFollowUpsLoading,
        sharedFollowUpsError,
        followUpsByContactId,
        onFollowUpsForContactUpdate,
    ]);

    const relatedDeals = useMemo(() => {
        if (!contactId) {
            return [];
        }

        return deals.filter((deal) => Number(deal?.contact_id ?? deal?.contactId) === Number(contactId));
    }, [deals, contactId]);

    const primaryRelatedDealTitle = useMemo(() => {
        const firstDeal = Array.isArray(relatedDeals) && relatedDeals.length > 0 ? relatedDeals[0] : null;
        return firstDeal?.title || firstDeal?.deal_name || firstDeal?.name || "No deal linked";
    }, [relatedDeals]);

    const nextFollowUpLabel = useMemo(() => {
        const rows = Array.isArray(followUps) ? followUps : [];
        const upcoming = rows
            .filter((item) => !item?.completed)
            .map((item) => {
                const dateValue = String(item?.follow_up_date || "").trim();
                const timeValue = String(item?.follow_up_time || "").trim();
                const parsed = new Date(timeValue ? `${dateValue}T${timeValue.length === 5 ? `${timeValue}:00` : timeValue}` : dateValue);
                return {
                    item,
                    time: Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime(),
                };
            })
            .filter((entry) => entry.time > 0)
            .sort((a, b) => a.time - b.time);

        const nextItem = upcoming[0]?.item || null;
        if (!nextItem) {
            return "-";
        }

        const datePart = nextItem.follow_up_date ? new Date(nextItem.follow_up_date).toLocaleDateString() : "";
        const timePart = nextItem.follow_up_time ? String(nextItem.follow_up_time).trim() : "";
        return [datePart, timePart].filter(Boolean).join(" ") || "-";
    }, [followUps]);

    const activityRows = useMemo(() => {
        const rows = [];

        (Array.isArray(calls) ? calls : []).forEach((call) => {
            const time = call?.call_at || call?.created_at || null;
            rows.push({
                id: `call-${call?.id}`,
                type: String(call?.call_type || "Call").replace(/^./, (letter) => letter.toUpperCase()),
                relatedDeal: call?.deal_title || primaryRelatedDealTitle,
                outcome: call?.outcome ? getPhoneCallOutcomeLabel(call.outcome) : String(call?.direction || call?.call_type || "-").replace(/^./, (letter) => letter.toUpperCase()),
                notes: call?.notes || "-",
                user: call?.user_name || profileName || "Unknown user",
                date: time,
                time,
                nextFollowUp: nextFollowUpLabel,
                sortValue: new Date(time || 0).getTime() || 0,
            });
        });

        (Array.isArray(emailLog.emailMessages) ? emailLog.emailMessages : []).forEach((message) => {
            const time = message?.sent_at || message?.received_at || message?.created_at || null;
            const isOutbound = String(message?.direction || "").toLowerCase() === "outbound";
            rows.push({
                id: `email-${message?.id || message?.graph_message_id}`,
                type: "Email",
                relatedDeal: primaryRelatedDealTitle,
                outcome: isOutbound ? "Sent" : "Received",
                notes: message?.body_text || message?.subject || "-",
                user: isOutbound ? (message?.from_name || profileName || "You") : (message?.from_name || message?.to_name || "Unknown user"),
                date: time,
                time,
                nextFollowUp: nextFollowUpLabel,
                sortValue: new Date(time || 0).getTime() || 0,
            });
        });

        (Array.isArray(meetings) ? meetings : []).forEach((meeting) => {
            const time = meeting?.start_time || meeting?.created_at || null;
            rows.push({
                id: `meeting-${meeting?.id}`,
                type: "Meeting",
                relatedDeal: primaryRelatedDealTitle,
                outcome: meeting?.outcome ? getMeetingOutcomeLabel(meeting.outcome) : getMeetingStatusLabel(meeting?.status || "scheduled"),
                notes: meeting?.subject || meeting?.description || "-",
                user: profileName || "You",
                date: time,
                time,
                nextFollowUp: nextFollowUpLabel,
                sortValue: new Date(time || 0).getTime() || 0,
            });
        });

        (Array.isArray(notes) ? notes : []).forEach((note) => {
            const time = note?.created_at || null;
            rows.push({
                id: `note-${note?.id}`,
                type: "Note",
                relatedDeal: primaryRelatedDealTitle,
                outcome: "Logged",
                notes: note?.content || "-",
                user: note?.user_name || profileName || "Unknown user",
                date: time,
                time,
                nextFollowUp: nextFollowUpLabel,
                sortValue: new Date(time || 0).getTime() || 0,
            });
        });

        (Array.isArray(followUps) ? followUps : []).forEach((item) => {
            const time = item?.created_at || item?.follow_up_date || null;
            rows.push({
                id: `followup-${item?.id}`,
                type: "Follow-Up",
                relatedDeal: primaryRelatedDealTitle,
                outcome: item?.completed ? "Complete" : "Pending",
                notes: item?.next_step || item?.notes || "-",
                user: item?.user?.name || profileName || "Unknown user",
                date: time,
                time,
                nextFollowUp: [item?.follow_up_date, item?.follow_up_time].filter(Boolean).join(" ") || nextFollowUpLabel,
                sortValue: new Date(time || 0).getTime() || 0,
            });
        });

        (Array.isArray(stageHistory) ? stageHistory : []).forEach((item) => {
            const time = item?.created_at || null;
            rows.push({
                id: `stage-${item?.id}`,
                type: "Stage Change",
                relatedDeal: primaryRelatedDealTitle,
                outcome: `${item?.previous_stage || "-"} to ${item?.new_stage || "-"}`,
                notes: "Contact stage updated",
                user: item?.changed_by_name || "Unknown user",
                date: time,
                time,
                nextFollowUp: nextFollowUpLabel,
                sortValue: new Date(time || 0).getTime() || 0,
            });
        });

        return rows
            .filter((row) => row.sortValue > 0)
            .sort((a, b) => b.sortValue - a.sortValue);
    }, [calls, emailLog.emailMessages, meetings, notes, followUps, stageHistory, nextFollowUpLabel, primaryRelatedDealTitle, profileName]);

    const activityLoading = callsLoading || meetingsLoading || notesLoading || followUpsLoading || stageHistoryLoading || emailLog.emailConversationsLoading || emailLog.emailMessagesLoading;
    const activityError = callsError || meetingsError || notesError || followUpsError || stageHistoryError || emailLog.emailConversationsError || emailLog.emailMessagesError || "";

    const totalDeals = relatedDeals.length;
    const totalDiscussions = activityRows.length;
    const totalDealValue = (() => {
        const value = relatedDeals.reduce((sum, deal) => {
            const cents = Number(deal?.amount_cents);
            if (Number.isFinite(cents) && cents > 0) {
                return sum + (cents / 100);
            }

            const amount = Number(deal?.deal_value);
            if (Number.isFinite(amount) && amount > 0) {
                return sum + amount;
            }

            return sum;
        }, 0);

        return `$${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
    })();

    const lastTouch = (() => {
        const latestCall = (Array.isArray(calls) ? calls : [])
            .map((call) => {
                const raw = call?.call_at || call?.created_at || null;
                const parsed = raw ? new Date(raw) : null;
                return {
                    raw,
                    parsed,
                    time: parsed && !Number.isNaN(parsed.getTime()) ? parsed.getTime() : 0,
                };
            })
            .sort((a, b) => b.time - a.time)[0];

        if (!latestCall || latestCall.time <= 0) {
            return "N/A";
        }

        return latestCall.parsed.toLocaleString([], {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });
    })();
    const {
        displayName,
        displayCompany,
        displayOwner,
        displayPhone,
        displayEmail,
        displayTitle,
        displayDealType,
        displayDealValueFormatted,
        displayDealStage,
        displayNextStep,
        displayLastTouch,
    } = getContactDetailDisplayData({
        contact,
        initialContact,
        relatedDeals,
    });

    const contactStageModel = useMemo(() => {
        return deriveContactStageModel({
            stage: displayDealStage,
            deals: relatedDeals,
        });
    }, [displayDealStage, relatedDeals]);

    const resolvedDealStage = contactStageModel?.effectiveLabel || displayDealStage;

    useEffect(() => {
        if (!setPageHeader || headerInitializedRef.current) {
            return;
        }

        headerInitializedRef.current = true;
        setPageHeader({
            back: true,
            onBack: () => onBackRef.current?.(),
        });
    }, [setPageHeader]);

    function handleDetailPanelAction(actionLabel) {
        const normalized = String(actionLabel || "").trim();
        const actionToTab = {
            Compose: "Emails",
            "Log Call": "Calls",
            "Add Note": "Notes",
            "Create Deal": "Deals",
            Meet: "Meetings",
            "Follow-Up": "Follow-Up",
        };

        const targetTab = actionToTab[normalized] || "Activity";
        setActiveTab(targetTab);
        setPanelActionRequest({
            type: normalized,
            nonce: Date.now(),
        });
    }

    const detailPanelFieldIcons = {
        phone: <FiPhone />,
        email: <FiMail />,
        title: <FiBriefcase />,
        dealType: <LuHandshake />,
        dealValue: <FiDollarSign />,
        lastTouch: <FaRegBuilding />,
        nextStep: <FiZap />,
    };

    const detailPanelActions = [
        { label: "Compose", icon: <FiMail /> },
        { label: "Log Call", icon: <FiPhone /> },
        { label: "Add Note", icon: <FaRegNoteSticky /> },
        { label: "Create Deal", icon: <FiBriefcase /> },
        { label: "Meet", icon: <FiCalendar /> },
        { label: "Follow-Up", icon: <MdOutlineTouchApp /> },
    ];

    function handleEditChange(e) {
        setEditForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    }

    async function handleSave(e) {
        e.preventDefault();
        setEditError("");
        try {
            setIsSaving(true);
            const updated = await updateContact(contactId, editForm);
            setContact(updated);
            setIsEditing(false);
        } catch (err) {
            setEditError(err.message || "Failed to save contact");
        } finally {
            setIsSaving(false);
        }
    }

    if (loading) {
        return (
            <div className="contact-detail-page">
                <div className="contact-detail-loading">Loading contact...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="contact-detail-page">
                <div className="contact-detail-error">{error}</div>
                <button type="button" className="contact-detail-back-btn" onClick={onBack}>← Back</button>
            </div>
        );
    }

    return (
        <Page>
            <div className="contact-detail-page">
                {/* Left panel */}
                <aside className="contact-left-panel">
                    <EntityDetailPanel
                        displayName={displayName}
                        displayCompany={displayCompany}
                        displayOwner={displayOwner}
                        editIcon={<MdOutlineModeEdit />}
                        fieldIcons={detailPanelFieldIcons}
                        isEditing={isEditing}
                        onStartEdit={() => setIsEditContactModalOpen(true)}
                        onSave={handleSave}
                        editError={editError}
                        editForm={editForm}
                        onEditChange={handleEditChange}
                        isSaving={isSaving}
                        onCancelEdit={() => setIsEditing(false)}
                        displayPhone={displayPhone}
                        displayEmail={displayEmail}
                        displayTitle={displayTitle}
                        displayDealType={displayDealType}
                        displayDealValueFormatted={displayDealValueFormatted}
                        displayDealStage={resolvedDealStage}
                        stageModel={contactStageModel}
                        displayLastTouch={displayLastTouch}
                        displayNextStep={displayNextStep}
                        actions={detailPanelActions}
                        onActionClick={handleDetailPanelAction}
                    />




                    <section className="contact-account-context" aria-labelledby="contact-account-context-heading">
                        <div className="contact-account-context-heading">
                            <div>
                                <p id="contact-account-context-heading">Account sales context</p>
                                <span>Reason and angle for contacting this account</span>
                            </div>
                            <button
                                type="button"
                                className="contact-account-link"
                                onClick={() => setIsAccountModalOpen(true)}
                                disabled={!contact?.account_id}
                            >
                                <FaRegBuilding aria-hidden="true" />
                                <span>{displayCompany}</span>
                            </button>
                        </div>
                        {accountAssessmentLoading ? <p className="contact-account-context-status">Loading sales context...</p> : null}
                        {accountAssessmentError ? <p className="contact-account-context-status contact-account-context-status--error">{accountAssessmentError}</p> : null}
                        {!accountAssessmentLoading && !accountAssessmentError ? (
                            <dl className="contact-account-context-details">
                                <div>
                                    <dt>Primary problem</dt>
                                    <dd>{accountAssessment?.primary_problem || "No primary problem recorded"}</dd>
                                </div>
                                <div>
                                    <dt>Call angle</dt>
                                    <dd>{accountAssessment?.call_angle || "No call angle recorded"}</dd>
                                </div>
                            </dl>
                        ) : null}
                    </section>

                    <div className="contact-detail-warnings">
                        <p className="contact-detail-warnings-title">Warnings</p>
                    </div>
                </aside>

                {/* Right panel */}
                <div className="contact-detail-main">
                    <ContactStage stage={resolvedDealStage} deals={relatedDeals} stageModel={contactStageModel} />
                    <QuickStatsWidget
                        variant="contact"
                        totalDeals={totalDeals}
                        totalDiscussions={totalDiscussions}
                        totalDealValue={totalDealValue}
                        lastTouch={lastTouch}
                    />

                    <TabView
                        tabs={TABS}
                        views={{
                            [activeTab]: (() => {
                                const TabComponent = TAB_COMPONENTS[activeTab];
                                return (
                                    <TabComponent
                                        contactId={contactId}
                                        contact={contact}
                                        calls={calls}
                                        callsLoading={callsLoading}
                                        callsError={callsError}
                                        activityRows={activityRows}
                                        activityLoading={activityLoading}
                                        activityError={activityError}
                                        {...emailLog}
                                        relatedDeals={relatedDeals}
                                        dealsLoading={dealsLoading}
                                        dealsError={dealsError}
                                        meetings={meetings}
                                        meetingsLoading={meetingsLoading}
                                        meetingsError={meetingsError}
                                        notes={notes}
                                        notesLoading={notesLoading}
                                        notesError={notesError}
                                        notesSaving={notesSaving}
                                        noteDraft={noteDraft}
                                        onNoteDraftChange={setNoteDraft}
                                        onAddNote={handleAddNote}
                                        followUps={followUps}
                                        followUpsLoading={followUpsLoading}
                                        followUpsError={followUpsError}
                                        panelActionRequest={panelActionRequest}
                                        onPanelActionHandled={() => setPanelActionRequest(null)}
                                        onComposeEmail={() => handleDetailPanelAction("Compose")}
                                        onRefreshTabData={() => {
                                            setTabDataVersion((value) => value + 1);
                                            refreshDeals();
                                            onFollowUpsRefresh?.();
                                        }}
                                    />
                                );
                            })(),
                        }}
                        activeTab={activeTab}
                        onTabChange={setActiveTab}
                        ariaLabel="Contact detail tabs"
                        tabsClassName="contact-detail-tabs"
                        contentClassName="contact-detail-tab-content"
                    />
                </div>
            </div>

            {isEditContactModalOpen ? (
                <NewContact
                    mode="edit"
                    contactId={contactId}
                    initialContact={contact}
                    onClose={() => setIsEditContactModalOpen(false)}
                    onUpdated={(updatedContact) => {
                        setContact(updatedContact);
                        setEditForm({
                            firstName: updatedContact?.first_name || "",
                            lastName: updatedContact?.last_name || "",
                            email: updatedContact?.email || "",
                            phone: updatedContact?.phone || "",
                            mobile: updatedContact?.mobile || updatedContact?.phone || "",
                            jobTitle: updatedContact?.title || "",
                            company: updatedContact?.company || updatedContact?.account || "",
                            account: updatedContact?.account || updatedContact?.company || "",
                        });
                        setIsEditContactModalOpen(false);
                    }}
                />
            ) : null}

            {isAccountModalOpen && contact?.account_id ? (
                <AccountDetailsModal
                    accountId={contact.account_id}
                    onClose={() => setIsAccountModalOpen(false)}
                />
            ) : null}

        </Page>
    );
}

export default ContactDetail;
